import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Building2, CalendarDays, Clock, FileText, Hash, MapPin, Pencil, UserRound } from "lucide-react";
import client, { apiErrorMessage } from "@/api/client";
import StatusBadge from "@/components/StatusBadge";
import { Field, IconInput } from "@/components/forms/FormField";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatRupees } from "./RoomsSection";

export const formatDay = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function EditProjectDialog({ open, setOpen, project }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: "", city: "", bhk: "", address: "" });

  useEffect(() => {
    if (open && project) setForm({ name: project.name || "", city: project.city || "", bhk: project.bhk || "", address: project.address || "" });
  }, [open, project]);

  const save = useMutation({
    mutationFn: () =>
      client.patch(`/projects/${project._id}`, {
        name: form.name.trim(),
        city: form.city.trim(),
        bhk: form.bhk.trim(),
        address: form.address.trim(),
      }),
    onSuccess: () => {
      toast.success("Project updated");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["project", project._id] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Failed to update project")),
  });

  const input = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="edit-project-dialog" className="sm:max-w-lg rounded-2xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (form.name.trim()) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-normal">Edit Project</DialogTitle>
            <DialogDescription>Update the project's name and location.</DialogDescription>
          </DialogHeader>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <Field label="Project Name" required htmlFor="project-name" className="sm:col-span-2">
              <IconInput id="project-name" icon={FileText} data-testid="project-name-input" {...input("name")} />
            </Field>
            <Field label="City" htmlFor="project-city">
              <IconInput id="project-city" icon={MapPin} data-testid="project-city-input" placeholder="e.g. Mumbai" {...input("city")} />
            </Field>
            <Field label="Property" htmlFor="project-bhk">
              <IconInput id="project-bhk" icon={Building2} data-testid="project-bhk-input" placeholder="e.g. 3 BHK" {...input("bhk")} />
            </Field>
            <Field label="Address" htmlFor="project-address" className="sm:col-span-2">
              <IconInput id="project-address" icon={MapPin} data-testid="project-address-input" {...input("address")} />
            </Field>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="project-save-button" disabled={!form.name.trim() || save.isPending} className="bg-accent hover:bg-accent/90 text-accent-foreground">
              {save.isPending ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
      <dt className="text-sm text-muted-foreground flex-1">{label}</dt>
      <dd className="text-sm text-foreground text-right truncate max-w-[60%]">{children}</dd>
    </div>
  );
}

export function ProjectDetailsCard({ project, customer, onEdit }) {
  const location = [project?.bhk, project?.city].filter(Boolean).join(", ");
  return (
    <section className="bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="project-details-card">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="font-serif text-xl text-foreground">Project Details</h2>
        <Button size="sm" variant="outline" data-testid="edit-project-button" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
        </Button>
      </div>
      <dl className="divide-y divide-border">
        <DetailRow icon={Hash} label="Project ID">{project?.code || "—"}</DetailRow>
        <DetailRow icon={UserRound} label="Customer">
          {customer ? (
            <Link to={`/customers/${customer._id}`} className="hover:text-accent">{customer.name}</Link>
          ) : (
            "—"
          )}
        </DetailRow>
        <DetailRow icon={MapPin} label="Location">{location || "—"}</DetailRow>
        <DetailRow icon={CalendarDays} label="Created On">{formatDay(project?.createdAt)}</DetailRow>
        <DetailRow icon={Clock} label="Last Updated">{formatDay(project?.updatedAt)}</DetailRow>
      </dl>
    </section>
  );
}

export function QuotationsCard({ quotations, canCreate, onCreate }) {
  const sorted = [...quotations].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
  const latest = sorted[0];
  return (
    <section className="bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="project-quotations-card">
      <div className="flex items-start gap-4">
        <span className="h-14 w-14 rounded-full bg-accent/10 text-accent flex items-center justify-center shrink-0">
          <FileText className="h-6 w-6" strokeWidth={1.6} />
        </span>
        <div className="min-w-0">
          <h2 className="font-serif text-xl text-foreground">Quotations</h2>
          <p className="text-sm text-muted-foreground">{latest ? "Latest quoted amount for this project." : "No quotation yet."}</p>
        </div>
      </div>

      {latest ? (
        <>
          <div className="mt-5 flex items-center justify-between gap-3">
            <p className="font-serif text-3xl text-foreground tabular-nums">{formatRupees(latest.grandTotal)}</p>
            <StatusBadge status={latest.status} />
          </div>
          <Link
            to={`/quotations/${latest._id}`}
            data-testid={`quotation-card-${latest._id}`}
            className="mt-4 flex items-center justify-center gap-1.5 rounded-xl border border-border py-2.5 text-sm font-medium text-foreground hover:border-foreground/30 hover:bg-secondary/50 transition-colors"
          >
            View Quotation <ArrowRight className="h-4 w-4" />
          </Link>
          {sorted.length > 1 && (
            <ul className="mt-4 pt-3 border-t border-border space-y-1">
              <li className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Earlier</li>
              {sorted.slice(1).map((q) => (
                <li key={q._id}>
                  <Link
                    to={`/quotations/${q._id}`}
                    data-testid={`quotation-card-${q._id}`}
                    className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 -mx-2 text-sm hover:bg-secondary/60"
                  >
                    <span className="text-foreground tabular-nums">{formatRupees(q.grandTotal)}</span>
                    <span className="text-xs text-muted-foreground">{q.status.replace(/_/g, " ").toLowerCase()} · {formatDay(q.updatedAt || q.createdAt)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <Button
          variant="outline"
          disabled={!canCreate}
          onClick={onCreate}
          className="mt-5 w-full"
          title={canCreate ? undefined : "Add products to the cart first"}
        >
          {canCreate ? "Create from cart" : "Add products to create one"}
        </Button>
      )}
    </section>
  );
}
